import { GraphQLFormattedError } from 'graphql'

type ErrorLike = {
  readonly message?: string
  readonly path?: ReadonlyArray<string | number>
  readonly extensions?: Record<string, unknown>
}

export type AuthErrorCandidate = {
  readonly graphQLErrors?: ReadonlyArray<ErrorLike>
  readonly networkError?: (Error & { statusCode?: number; result?: unknown }) | null
}

const UNAUTHENTICATED = 'UNAUTHENTICATED'

// Until the API's error contract flag is on, an expired or invalid token comes back as
// HTTP 500 "Context creation failed: <jsonwebtoken or AuthService message>".
const LEGACY_CONTEXT_PREFIX = 'Context creation failed:'
const LEGACY_TOKEN_FAILURES = [
  'jwt expired',
  'jwt malformed',
  'invalid signature',
  'jwt not active',
  'Error or malformed token',
]

function errorsInBody(result: unknown): ReadonlyArray<GraphQLFormattedError> {
  const errors = (result as { errors?: unknown } | null | undefined)?.errors
  return Array.isArray(errors) ? errors : []
}

function isLegacyTokenFailure(error: ErrorLike): boolean {
  const message = error.message ?? ''
  return message.startsWith(LEGACY_CONTEXT_PREFIX) && LEGACY_TOKEN_FAILURES.some((text) => message.includes(text))
}

export function isUnauthenticatedError({ graphQLErrors, networkError }: AuthErrorCandidate): boolean {
  if (networkError?.statusCode === 401) return true

  const bodyErrors = errorsInBody(networkError?.result)
  // Only request-level errors (no `path`) mean the session is gone. A resolver error with
  // UNAUTHENTICATED is a per-field authorization failure and must not log the user out.
  const requestErrors = [...(graphQLErrors ?? []), ...bodyErrors].filter((error) => !error.path?.length)
  if (requestErrors.some((error) => error.extensions?.code === UNAUTHENTICATED)) return true

  return networkError?.statusCode === 500 && bodyErrors.some(isLegacyTokenFailure)
}
